import React, { useState, useEffect, useContext } from "react";
import axios from 'axios';
import styles from "./styles/ClassroomAnnouncements.module.css";
import TextEditor from "../texteditor/TextEditor";
import { ClassroomContext } from "../../context/ClassroomContext";
import AuthContext from "../../context/AuthContext";
import EditAnnouncementModal from './EditAnnouncementModal';
import { transformIframes } from '../../utils/utils';

const ClassroomAnnouncements = () => {
    const { classroom, updateClassroom, isTeacher } = useContext(ClassroomContext);
    const { userId, user } = useContext(AuthContext);

    const [announcements, setAnnouncements] = useState(classroom?.announcements || []);
    const [students, setStudents] = useState(classroom?.students || []);

    const [newAnnouncementText, setNewAnnouncementText] = useState(""); 
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [announcementToEdit, setAnnouncementToEdit] = useState(null);
    const [openedDropdown, setOpenedDropdown] = useState(null); 

    

    useEffect(() => {
        if (classroom?.announcements) {
            setAnnouncements(classroom.announcements);
        }
        if (classroom?.students) {
            setStudents(classroom.students);
        }
    }, [classroom]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (openedDropdown && !event.target.closest(`.${styles.optionsDropdown}`) && !event.target.closest(`.${styles.optionsButton}`)) {
                setOpenedDropdown(null);
            }
        };

        document.addEventListener("click", handleClickOutside);
        return () => {
            document.removeEventListener("click", handleClickOutside);
        };
    }, [openedDropdown]);

    const cleanText = (text) => {
        let cleanedText = text.replace(/<[^>]*>?/gm, '');
        cleanedText = cleanedText.replace(/&nbsp;/gi, ' ');
        return cleanedText;
    };

    const handleNewAnnouncement = async () => {
        if (newAnnouncementText.trim() === "") return;

        const cleanedAnnouncementText = cleanText(newAnnouncementText);
        const newAnnouncement = {
            poster: { _id: userId, username: user.username }, 
            text: cleanedAnnouncementText,
        };

        try {
            const updatedAnnouncements = [...announcements, newAnnouncement];
            setAnnouncements(updatedAnnouncements);

            await updateClassroom(classroom._id, { announcements: updatedAnnouncements });

            setNewAnnouncementText("");

            
            await axios.post(`${process.env.REACT_APP_API_URL_LOCAL}/api/notifications/sendAnnouncementToStudents`, {
                students: students, 
                classroomId: classroom._id,
                classroomName: classroom.name,
                announcementText: cleanedAnnouncementText,
            });

        } catch (err) {
            console.error("Error posting announcement:", err);
        }
    };

    const handleStopEditing = async (id, newText) => {
        if (newText.trim() === '') return;

        const updatedAnnouncements = announcements.map((announcement) =>
            announcement._id === id ? { ...announcement, text: newText } : announcement
        );

        try {
            setAnnouncements(updatedAnnouncements);

            await axios.patch(`${process.env.REACT_APP_API_URL_LOCAL}/api/classrooms/${classroom._id}`, {
                announcements: updatedAnnouncements
            });
        } catch (err) {
            console.error("An error occurred while updating the announcement:", err);
        }
    };

    const handleDeleteAnnouncement = async (announcementToDelete) => {
        const updatedAnnouncements = announcements.filter(
            (announcement) => announcement._id !== announcementToDelete._id
        );

        try {
            setAnnouncements(updatedAnnouncements);

            await axios.patch(`${process.env.REACT_APP_API_URL_LOCAL}/api/classrooms/${classroom._id}`, {
                announcements: updatedAnnouncements
            });
        } catch (err) {
            console.error("An error occurred while deleting the announcement:", err);
        }
    };

    
    const handleTextEditorChange = (data) => {
        setNewAnnouncementText(data);
    };

    return (
        <div className={styles.container}> {/* Changed class */}
            {isEditModalOpen && (
                <EditAnnouncementModal
                    isOpen={isEditModalOpen}
                    onClose={() => setIsEditModalOpen(false)}
                    announcement={announcementToEdit}
                    handleStopEditing={handleStopEditing}
                />
            )}
            {isTeacher && (
                <div className={styles.createCard}> {/* Changed class */}
                    <TextEditor
                        initialData={newAnnouncementText}
                        onChange={handleTextEditorChange}
                    />
                    <div className={styles.buttonWrapper}> {/* Changed class */}
                        <button
                            onClick={handleNewAnnouncement}
                            
                            className={`${styles.button} ${styles.buttonPrimary}`}
                        >
                            Post Announcement
                        </button>
                    </div>
                </div>
            )}
            <div className={styles.grid}> {/* Changed class */}
                {[...announcements].reverse().map((announcement, i) => (
                    <div key={announcement._id || i} className={styles.card}> {/* Changed class */}
                        {/* Added cardHeader wrapper */}
                        <div className={styles.cardHeader}>
                            {/* Kept original username div, class updated implicitly by CSS if needed, but structure matches */}
                            <div className={styles.username}>{announcement.poster.username}</div>
                            {isTeacher && (
                                <button
                                    className={styles.optionsButton} 
                                    onClick={() => setOpenedDropdown(openedDropdown === announcement._id ? null : announcement._id)}
                                >
                                    ⋮
                                </button>
                            )}
                        </div>
                        {/* Added cardContent wrapper */}
                        <div className={styles.cardContent}>
                            {/* Kept original content paragraph */}
                            <p dangerouslySetInnerHTML={{ __html: transformIframes(announcement.text) }} />
                        </div>
    
                        {/* Kept original dropdown logic, updated container and item classes */}
                        {openedDropdown === announcement._id && (
                            <div className={styles.optionsDropdown}> {/* Changed class */}
                                <div
                                    className={styles.optionsDropdownItem} 
                                    onClick={() => {
                                        setAnnouncementToEdit(announcement);
                                        setIsEditModalOpen(true);
                                        setOpenedDropdown(null);
                                    }}
                                >
                                    Edit
                                </div>
                                <div
                                    className={styles.optionsDropdownItem} 
                                    onClick={() => handleDeleteAnnouncement(announcement)}
                                >
                                    Delete
                                </div>
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default ClassroomAnnouncements;
